// frontend/src/pages/Blog.jsx
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

export default function Blog() {
  const posts = useMemo(
    () => [
      {
        title: "Browser Notifications + Mobile App Roadmap",
        summary:
          "Real-time browser notifications are rolling out across Coinzetto. We also share our Android/iOS roadmap to bring the full web experience to mobile.",
        date: "August 30, 2025",
        link: "#",
        tags: ["Product", "Mobile", "Notifications"],
        isNew: true,
      },
      {
        title: "P2P: Persistent Notifications & Bell Dropdown (Desktop + Mobile)",
        summary:
          "Your P2P trade alerts now persist in a notification dropdown with unread counts. Works on desktop and mobile, with click-through to the trade.",
        date: "August 28, 2025",
        link: "#",
        tags: ["P2P", "Notifications", "UX"],
        isNew: true,
      },
      {
        title: "Disputes in P2P Trades: Raise, Resolve, and Moderator Notes",
        summary:
          "We launched dispute handling for P2P trades. Buyers and sellers can raise disputes after payment; moderators can resolve with system notes and audit trail.",
        date: "August 06, 2025",
        link: "#",
        tags: ["P2P", "Safety", "Compliance"],
      },
      {
        title: "P2P Profile: Public Pages, Feedback Stats, and Trade History",
        summary:
          "Vendor-style public profiles with trust signals, feedback, and offer lists—designed for clarity and quick action from market pages.",
        date: "July 19, 2025",
        link: "#",
        tags: ["P2P", "UX"],
      },
      {
        title: "Hot Wallet Balances Tab in Admin",
        summary:
          "Operations now get a single pane of glass for TRX/USDT (TRC20/ERC20/BEP20), BTC, ETH, and BNB hot-wallet balances and addresses.",
        date: "July 02, 2025",
        link: "#",
        tags: ["Admin", "Wallets", "Ops"],
      },
      {
        title: "Is Crypto Legal in India in 2025?",
        summary:
          "Understand the landscape: registration, KYC/AML obligations, and how exchanges operate under current rules and guidance.",
        date: "July 01, 2025",
        link: "#",
        tags: ["Regulation", "India"],
      },
      {
        title: "Coinzetto Launches Instant INR Withdrawals",
        summary:
          "Withdraw INR to your bank in 2–3 hours during banking hours. Here’s how the flow works and what to expect.",
        date: "June 24, 2025",
        link: "#",
        tags: ["INR", "Product"],
      },
      {
        title: "Beginner’s Guide: How to Use Quick Trade on Coinzetto",
        summary:
          "A step-by-step walkthrough to instantly buy or sell crypto using Quick Trade—great for first-time traders.",
        date: "June 10, 2025",
        link: "#",
        tags: ["Guides", "Trading"],
      },
      {
        title: "Understanding TDS and GST on Crypto in India",
        summary:
          "We break down 1% TDS, 18% GST on platform fees, and how all charges are shown before you confirm a trade.",
        date: "May 28, 2025",
        link: "#",
        tags: ["Taxes", "India", "Guides"],
      },
    ],
    []
  );

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return posts;
    const s = q.toLowerCase();
    return posts.filter(
      (p) =>
        p.title.toLowerCase().includes(s) ||
        p.summary.toLowerCase().includes(s) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(s))
    );
  }, [q, posts]);

  return (
    <motion.div
      className="min-h-screen bg-blue-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 pb-12 md:pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur shadow-md border-b-4 border-blue-600 dark:border-blue-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-16 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 dark:text-blue-300 mb-3 md:mb-4">
            Coinzetto Blog
          </h1>
          <p className="text-blue-700/90 dark:text-blue-200/90 text-sm sm:text-lg md:text-xl max-w-2xl mx-auto">
            Stay informed with crypto news, trading tips, and platform updates.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 md:mt-16">
        {/* Search */}
        <div className="mb-6 md:mb-8">
          <label htmlFor="blog-search" className="sr-only">
            Search posts
          </label>
          <input
            id="blog-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search posts (e.g., P2P, GST, notifications)…"
            className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 text-sm sm:text-base outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500"
          />
        </div>

        {/* Blog Posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {filtered.map((post, index) => (
            <motion.article
              key={`${post.title}-${index}`}
              whileHover={{ y: -3 }}
              transition={{ type: "spring", stiffness: 250, damping: 20 }}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {post.date}
                </p>
                {post.isNew && (
                  <span className="ml-2 inline-flex items-center rounded-full border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-blue-700 dark:text-blue-300">
                    New
                  </span>
                )}
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-blue-300 mb-1.5">
                {post.title}
              </h2>
              <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 mb-3">
                {post.summary}
              </p>
              {post.tags?.length ? (
                <div className="flex flex-wrap gap-2 mb-3">
                  {post.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200 px-2.5 py-0.5 text-[10px] sm:text-xs"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
              <a
                href={post.link}
                className="inline-flex items-center text-blue-600 dark:text-blue-400 font-medium text-sm sm:text-base hover:underline underline-offset-4"
              >
                Read more →
              </a>
            </motion.article>
          ))}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="mt-10 text-center text-sm sm:text-base text-slate-600 dark:text-slate-300">
            No posts match “{q}”. Try another keyword.
          </div>
        )}

        {/* CTA */}
        <section className="text-center pt-10 md:pt-16">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-2 md:mb-4">
            Want to Learn More?
          </h2>
          <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 mb-4 md:mb-6">
            Check out our{" "}
            <a
              href="/learn"
              className="text-blue-600 dark:text-blue-400 underline underline-offset-4 hover:no-underline"
            >
              Learn
            </a>{" "}
            section or get started with real trading today.
          </p>
          <a
            href="/signup"
            className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 sm:px-8 sm:py-3 text-sm sm:text-base font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:focus-visible:ring-blue-500 shadow"
          >
            Open Your Account
          </a>
        </section>
      </main>
    </motion.div>
  );
}
